import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  ITaskAttachment,
  ITaskManagement,
  RepetitiveTaskDays,
  RepetitiveTaskType,
} from "../../models/taskManagement.model";
import Button, { ButtonType } from "../../assets/Button";
import { FormChangeEvent } from "../../utils/taskManagementUtils";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";
import UserSelectorSearch from "../Users/UserSelectorSearch";
import ToggleSwitch from "../Users/ToggleSwitch";
import TiptapEditor from "../Assets/TiptapEditor";

interface AddTaskManagerModalProps {
  form: ITaskManagement;
  setForm: React.Dispatch<React.SetStateAction<ITaskManagement>>;
  onCancel: () => void;
  handleChange: (e: FormChangeEvent) => void;
  handleRemoveAttachment?: (attachmentId: string) => void;
  handleCreateTask: (files: File[], keepOpen?: boolean) => Promise<void>;
  isEdit?: boolean;
  isRepetitiveTask: boolean;
  setIsRepetitiveTask: (isRepetitive: boolean) => void;
  selectedDays: RepetitiveTaskDays[];
  setSelectedDays: (days: RepetitiveTaskDays[]) => void;
  repetitionType: RepetitiveTaskType;
  handleDaySelection: (day: RepetitiveTaskDays) => void;
  handleRepetitionTypeChange: (type: RepetitiveTaskType) => void;
  monthlyDate: Date | null;
  setMonthlyDate: (date: Date | null) => void;
  yearlyDate: Date | null;
  setYearlyDate: (date: Date | null) => void;
  noOfDays: number;
  setNoOfDays: (days: number) => void;
  files: FileList | null;
  setFiles: React.Dispatch<React.SetStateAction<FileList | null>>;
}

const AddTaskManagerModal: React.FC<AddTaskManagerModalProps> = ({
  form,
  setForm,
  onCancel,
  handleChange,
  handleRemoveAttachment,
  handleCreateTask,
  isEdit,
  isRepetitiveTask,
  setIsRepetitiveTask,
  selectedDays,
  setSelectedDays,
  repetitionType,
  handleDaySelection,
  handleRepetitionTypeChange,
  monthlyDate,
  setMonthlyDate,
  yearlyDate,
  setYearlyDate,
  noOfDays,
  setNoOfDays,
  files,
  setFiles,
}) => {
  const users = useSelector((state: RootState) => state.users.user);
  const metadata = useSelector(
    (state: RootState) => state.TaskManagement.metadata
  );

  const rawTypes = metadata?.type;
  const rawPriorities = metadata?.priority;

  const types = Array.isArray(rawTypes) ? rawTypes[0] : rawTypes || {};
  const priorities = Array.isArray(rawPriorities)
    ? rawPriorities[0]
    : rawPriorities || {};

  const handleWheelPrevent = usePreventWheelChange();

  const formatDateForInput = (date?: Date | string) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  const handleDaySelectionAll = (select: boolean) => {
    if (select) {
      setSelectedDays(
        Object.values(RepetitiveTaskDays).filter(
          (day) => day !== RepetitiveTaskDays.sunday
        )
      );
    } else {
      setSelectedDays([]);
    }
  };

  const inputBaseClasses =
    "w-full p-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 outline-none bg-white text-gray-900 placeholder-gray-500";
  const selectClasses =
    "w-full p-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 outline-none bg-white text-gray-900 cursor-pointer";
  const labelClasses = "block text-xs font-semibold text-gray-700 mb-2";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="px-8 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isEdit ? "Update Task" : "Create New Task"}{" "}
              </h2>

              {!isEdit && (
                <p className="text-xs text-gray-600 mt-1">
                  Fill in the details below to create a new task
                </p>
              )}
            </div>
            <button
              onClick={onCancel}
              className="w-8 h-8 rounded-full bg-white shadow-md hover:shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-800 transition-all duration-200 hover:scale-105"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
          <div className="space-y-6">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                <div className="lg:col-span-2">
                  <label className={labelClasses}>Task Name</label>
                  <input
                    name="taskName"
                    value={form.taskName}
                    onChange={handleChange}
                    placeholder="Enter task name"
                    className={inputBaseClasses}
                  />
                </div>

                <UserSelectorSearch
                  users={users}
                  label="Assignee"
                  value={form.assignee}
                  onChange={(val) =>
                    handleChange({
                      target: { name: "assignee", value: val },
                    } as any)
                  }
                />

                <div>
                  <label className={labelClasses}>Task Type</label>
                  <select
                    name="type"
                    value={form.type}
                    onChange={handleChange}
                    className={selectClasses}
                  >
                    <option value="" disabled>
                      Select task type
                    </option>
                    {Object.entries(types).map(([key, label]) => (
                      <option key={key} value={key}>
                        {(label as string).capitalizeWords()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClasses}>Priority</label>
                  <select
                    name="priority"
                    value={form.priority ?? ""}
                    onChange={handleChange}
                    className={selectClasses}
                  >
                    <option value="" disabled>
                      Select priority level
                    </option>
                    {Object.entries(priorities).map(([key, label]) => (
                      <option key={key} value={key}>
                        {(label as string).capitalizeWords()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <UserSelectorSearch
              users={users}
              label="Followers"
              value={form.followers}
              onChange={(val) =>
                handleChange({
                  target: { name: "followers", value: val },
                } as any)
              }
              multiple
            />
            {/* Timeline Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Timeline
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className={labelClasses}>Start Date</label>
                  <input
                    name="startDate"
                    type="date"
                    value={formatDateForInput(form.startDate ?? new Date())}
                    onChange={handleChange}
                    className={inputBaseClasses}
                  />
                </div>

                <div>
                  <label className={labelClasses}>End Date</label>
                  <input
                    name="endDate"
                    type="date"
                    value={formatDateForInput(form.endDate ?? new Date())}
                    onChange={handleChange}
                    className={inputBaseClasses}
                  />
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                Description
              </h3>

              <div>
                <TiptapEditor form={form} setForm={setForm} />
              </div>
            </div>

            <div className="space-y-2">
              {(!isEdit ||
                (Array.isArray(form.attachments) &&
                  form.attachments.length > 0)) && (
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  Attachments
                </h3>
              )}

              {/* File Upload */}
              {!isEdit && (
                <div className="border border-dashed border-gray-200 rounded-lg p-3 hover:border-indigo-300 transition-colors">
                  <label className="cursor-pointer flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-indigo-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                      />
                    </svg>
                    <span className="text-xs font-medium text-gray-900">
                      Choose files
                    </span>
                    <span className="text-xs text-gray-500">JPG, PNG, PDF</span>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => setFiles(e.target.files)}
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.pdf"
                    />
                  </label>
                </div>
              )}

              {/* New Files */}
              {files && files.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
                  {Array.from(files).map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-xs text-blue-700"
                    >
                      <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                      <span className="truncate">{file.name}</span>
                      <span className="text-blue-500">
                        ({(file.size / 1024 / 1024).toFixed(1)}MB)
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Existing Attachments */}
              {Array.isArray(form.attachments) &&
                form.attachments.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                    <div className="space-y-1">
                      {form.attachments.map(
                        (file: ITaskAttachment, idx: number) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between group"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-gray-900 hover:text-indigo-600 truncate"
                                title={file.name || `Attachment ${idx + 1}`}
                              >
                                {file.name || `Attachment ${idx + 1}`}
                              </a>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveAttachment &&
                                handleRemoveAttachment(file._id || "")
                              }
                              className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all p-1"
                            >
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>
            {!isEdit && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ToggleSwitch
                    checked={isRepetitiveTask}
                    onChange={setIsRepetitiveTask}
                    label="Repetitive Settings"
                    description="Enable if this is a recurring task"
                    sizeVariant="xs"
                    showIcon={false}
                    showStatusText={true}
                  />
                </div>

                <div className="space-y-2">
                  {isRepetitiveTask && (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-4">
                      <div>
                        <label className={labelClasses}>Repetition Type</label>
                        <select
                          value={repetitionType}
                          onChange={(e) =>
                            handleRepetitionTypeChange(
                              e.target.value as RepetitiveTaskType
                            )
                          }
                          className={selectClasses}
                        >
                          {Object.values(RepetitiveTaskType).map((val) => (
                            <option key={val} value={val}>
                              {val.capitalizeWords()}
                            </option>
                          ))}
                        </select>
                      </div>

                      {repetitionType === RepetitiveTaskType.weekly && (
                        <div>
                          <label className={labelClasses}>Repeat on Days</label>

                          {/* Daily Checkbox */}
                          <label className="flex items-center gap-2 mb-2">
                            <input
                              type="checkbox"
                              checked={
                                selectedDays.length ===
                                Object.values(RepetitiveTaskDays).filter(
                                  (d) => d !== RepetitiveTaskDays.sunday
                                ).length
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleDaySelectionAll(true);
                                } else {
                                  handleDaySelectionAll(false);
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-xs font-medium text-gray-700">
                              Daily (Mon-Sat)
                            </span>
                          </label>
                          <div className="grid grid-cols-7 gap-2 mt-2">
                            {Object.values(RepetitiveTaskDays).map((day) => (
                              <label
                                key={day}
                                className={`flex items-center justify-center p-2 rounded-lg border border-gray-200 cursor-pointer transition-all ${
                                  selectedDays.includes(day)
                                    ? "bg-blue-100 border-blue-300 text-blue-700"
                                    : "bg-white text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDays.includes(day)}
                                  onChange={() => handleDaySelection(day)}
                                  className="hidden"
                                />
                                <span className="text-xs font-medium">
                                  {day.charAt(0).capitalizeWords() +
                                    day.slice(1, 3)}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {(repetitionType === RepetitiveTaskType.monthly ||
                        repetitionType === RepetitiveTaskType.yearly) && (
                        <div>
                          <label className={labelClasses}>
                            Select Date ({repetitionType})
                          </label>
                          <input
                            type="date"
                            value={
                              repetitionType === RepetitiveTaskType.monthly
                                ? formatDateForInput(monthlyDate ?? "")
                                : formatDateForInput(yearlyDate ?? "")
                            }
                            onChange={(e) =>
                              repetitionType === RepetitiveTaskType.monthly
                                ? setMonthlyDate(
                                    e.target.value
                                      ? new Date(e.target.value)
                                      : null
                                  )
                                : setYearlyDate(
                                    e.target.value
                                      ? new Date(e.target.value)
                                      : null
                                  )
                            }
                            className={inputBaseClasses}
                          />
                        </div>
                      )}
                      <div>
                        <label className={labelClasses}>
                          Number of Days (Optional)
                        </label>
                        <input
                          type="number"
                          value={noOfDays}
                          onWheel={handleWheelPrevent}
                          onChange={(e) =>
                            setNoOfDays(Number(e.target.value) || 0)
                          }
                          className={inputBaseClasses}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-4">
          <Button
            type={ButtonType.Cancel}
            onClick={onCancel}
            className="text-sm "
          >
            Cancel
          </Button>

          <Button
            type={ButtonType.Submit}
            onClick={() => handleCreateTask(files ? Array.from(files) : [])}
            className="text-sm "
          >
            {isEdit ? "Update Task" : "Create Task"}
          </Button>
          {!isEdit && (
            <Button
              type={ButtonType.Extra}
              onClick={() =>
                handleCreateTask(files ? Array.from(files) : [], true)
              }
              className="text-sm"
            >
              Create Another Task
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTaskManagerModal;
